import { SchLayerType } from '@cadstrom/sigma-engine-shared';

import { ColorSchemes } from './cadstrom-color-provider.js';

import type { CadTool } from '@cadstrom/sigma-engine-shared';
import type { IColorScheme, IColorSchemeSet } from './cadstrom-color-provider.js';

/** One tool's schematic canvas colours in one theme mode. */
export interface CadToolPalette {
	canvas: string;
	gridMinor: string;
	gridMajor: string;
	wire: string;
	bus: string;
	junction: string;
	symbol: string;
	/** Fill behind symbol bodies; fully transparent for tools that leave bodies unfilled. */
	symbolFill: string;
	pin: string;
	pinName: string;
	pinNumber: string;
	label: string;
	reference: string;
	value: string;
	note: string;
}

/** A tool's light and dark schematic palettes. */
export interface CadToolPaletteSet {
	name: string;
	light: CadToolPalette;
	dark: CadToolPalette;
}

const UNFILLED = 'rgba(0,0,0,0)';

/**
 * Schematic palettes for each authoring tool. Light and dark come from the tool's own built-in themes
 * where it has both; Altium has no dark sheet, so its dark palette keeps Altium's colour roles on a
 * dark sheet. Xpedition has no palette yet and takes the default scheme.
 */
export const CAD_TOOL_PALETTES: Readonly<Record<Exclude<CadTool, 'cadstrom' | 'xpedition'>, CadToolPaletteSet>> = {
	kicad: {
		name: 'KiCad',
		// KiCad's built-in default theme.
		light: {
			canvas: '#F5F4EF',
			gridMinor: '#B5B5B5',
			gridMajor: '#B5B5B5',
			wire: '#009600',
			bus: '#000084',
			junction: '#009600',
			symbol: '#840000',
			symbolFill: '#FFFFC2',
			pin: '#840000',
			pinName: '#006464',
			pinNumber: '#A90000',
			label: '#0F0F0F',
			reference: '#006464',
			value: '#006464',
			note: '#0000C2',
		},
		// "Behave Dark", the dark theme from KiCad's own plugin and content repository.
		dark: {
			canvas: '#232932',
			gridMinor: '#848484',
			gridMajor: '#848484',
			wire: '#8DD272',
			bus: '#4487AA',
			junction: '#8DD272',
			symbol: '#C4626B',
			symbolFill: '#513C56',
			pin: '#C4626B',
			pinName: '#669696',
			pinNumber: '#C4626B',
			label: '#4487AA',
			reference: '#A683E1',
			value: '#669696',
			note: '#4487AA',
		},
	},
	altium: {
		name: 'Altium',
		// Altium Designer's default sheet and object colours.
		light: {
			canvas: '#FFFCF8',
			gridMinor: '#F0F0EB',
			gridMajor: '#DCDCD7',
			wire: '#000080',
			bus: '#000080',
			junction: '#000080',
			symbol: '#800000',
			symbolFill: '#FFFFB0',
			pin: '#000000',
			pinName: '#000000',
			pinNumber: '#000000',
			label: '#800000',
			reference: '#000080',
			value: '#000080',
			note: '#000080',
		},
		dark: {
			canvas: '#1C1B19',
			gridMinor: '#2A2926',
			gridMajor: '#3A3833',
			wire: '#8C9BFF',
			bus: '#8C9BFF',
			junction: '#8C9BFF',
			symbol: '#E06A62',
			symbolFill: '#45402A',
			pin: '#D9D9D9',
			pinName: '#D9D9D9',
			pinNumber: '#D9D9D9',
			label: '#E8857C',
			reference: '#9DAEFF',
			value: '#9DAEFF',
			note: '#9DAEFF',
		},
	},
	orcad: {
		name: 'OrCAD',
		// OrCAD Capture's Legacy theme, the classic look.
		light: {
			canvas: '#FFFFFF',
			gridMinor: '#C0C0C0',
			gridMajor: '#C0C0C0',
			wire: '#800040',
			bus: '#0000FF',
			junction: '#FF00FF',
			symbol: '#0000FF',
			symbolFill: UNFILLED,
			pin: '#FF0000',
			pinName: '#004080',
			pinNumber: '#000000',
			label: '#FF0000',
			reference: '#000080',
			value: '#000080',
			note: '#000000',
		},
		// OrCAD Capture's own Dark theme.
		dark: {
			canvas: '#000000',
			gridMinor: '#434343',
			gridMajor: '#434343',
			wire: '#BCFF00',
			bus: '#00C8FF',
			junction: '#FF0000',
			symbol: '#CC8008',
			symbolFill: UNFILLED,
			pin: '#AA8844',
			pinName: '#E2E2E2',
			pinNumber: '#E2E2E2',
			label: '#E2E2E2',
			reference: '#FFFFFF',
			value: '#E2E2E2',
			note: '#E2E2E2',
		},
	},
	eagle: {
		name: 'EAGLE',
		// EAGLE's White palette.
		light: {
			canvas: '#FFFFFF',
			gridMinor: '#E6E6E6',
			gridMajor: '#E6E6E6',
			wire: '#4BA54B',
			bus: '#4B4BA5',
			junction: '#4BA54B',
			symbol: '#A54B4B',
			symbolFill: UNFILLED,
			pin: '#A54B4B',
			pinName: '#A54B4B',
			pinNumber: '#A54B4B',
			label: '#838383',
			reference: '#838383',
			value: '#838383',
			note: '#838383',
		},
		// EAGLE's Black palette.
		dark: {
			canvas: '#000000',
			gridMinor: '#272727',
			gridMajor: '#272727',
			wire: '#238D23',
			bus: '#23238D',
			junction: '#238D23',
			symbol: '#8D2323',
			symbolFill: UNFILLED,
			pin: '#8D2323',
			pinName: '#8D2323',
			pinNumber: '#8D2323',
			label: '#8D8D8D',
			reference: '#8D8D8D',
			value: '#8D8D8D',
			note: '#8D8D8D',
		},
	},
	allegro: {
		name: 'Allegro X System Capture',
		// System Capture's Light sheet.
		light: {
			canvas: '#F8F8F8',
			gridMinor: '#DCE1E5',
			gridMajor: '#DCE1E5',
			wire: '#2E6E00',
			bus: '#8C6400',
			junction: '#2E6E00',
			symbol: '#505050',
			symbolFill: UNFILLED,
			pin: '#505050',
			pinName: '#202020',
			pinNumber: '#202020',
			label: '#202020',
			reference: '#A87800',
			value: '#202020',
			note: '#6A3CC8',
		},
		// System Capture's default Dark sheet.
		dark: {
			canvas: '#202830',
			gridMinor: '#2C3C48',
			gridMajor: '#2C3C48',
			wire: '#7CA014',
			bus: '#DCA820',
			junction: '#7CA014',
			symbol: '#AC8824',
			symbolFill: UNFILLED,
			pin: '#845C1C',
			pinName: '#E8E8E8',
			pinNumber: '#E8D44D',
			label: '#FFFFFF',
			reference: '#FFFFFF',
			value: '#FFFFFF',
			note: '#B48CF0',
		},
	},
};

/**
 * Expands a tool palette into a full colour scheme. Schematic layers, the canvas and the grid take the
 * tool's colours; PCB layers and the interaction colours (selection, overlays, rulers) keep the base
 * scheme's.
 *
 * @param palette - The tool's colours for one theme mode.
 * @param base - The scheme for the same theme mode to inherit everything else from.
 * @returns A scheme that colours schematics like the tool.
 */
export function buildCadToolColorScheme(palette: CadToolPalette, base: IColorScheme): IColorScheme {
	const byType: Record<SchLayerType, string> = {
		[SchLayerType.Wire]: palette.wire,
		[SchLayerType.Bus]: palette.bus,
		[SchLayerType.Junction]: palette.junction,
		[SchLayerType.BusJunction]: palette.bus,
		[SchLayerType.Symbol]: palette.symbol,
		[SchLayerType.Background]: palette.symbolFill,
		[SchLayerType.Pin]: palette.pin,
		[SchLayerType.PinName]: palette.pinName,
		[SchLayerType.PinNumber]: palette.pinNumber,
		[SchLayerType.Label]: palette.label,
		[SchLayerType.NameLabel]: palette.reference,
		[SchLayerType.ValueLabel]: palette.value,
		[SchLayerType.Other]: palette.note,
		[SchLayerType.Image]: '#FFFFFF',
	};

	return {
		...base,
		schLayers: {
			colorsByType: Object.values(SchLayerType).map((layerType) => ({ layerType, colors: [byType[layerType]] })),
		},
		schematicTools: {
			canvas: { background: palette.canvas },
			grid: { default: palette.gridMinor, major: palette.gridMajor },
		},
	};
}

const CAD_TOOL_COLOR_SCHEMES = new Map<CadTool, IColorSchemeSet>(
	Object.entries(CAD_TOOL_PALETTES).map(([tool, palettes]) => [
		tool as CadTool,
		{
			name: palettes.name,
			colorSchemes: {
				light: buildCadToolColorScheme(palettes.light, ColorSchemes.Default.colorSchemes.light),
				dark: buildCadToolColorScheme(palettes.dark, ColorSchemes.Default.colorSchemes.dark),
			},
		},
	]),
);

/**
 * Picks the colour scheme that makes a project look like the tool that authored it.
 *
 * @param tool - The project's authoring tool, or `undefined` when unknown.
 * @returns The tool's scheme, or the default Cadstrom scheme for Cadstrom-made or unknown projects.
 */
export function getColorSchemeForCadTool(tool: CadTool | undefined): IColorSchemeSet {
	return (tool && CAD_TOOL_COLOR_SCHEMES.get(tool)) ?? ColorSchemes.Default;
}
